import React, { useState, useEffect, ReactNode, useMemo } from 'react';
import Popper, { Placement } from '../Popper';
import { Wrap, Block, Corner, Arrow, ArrowVert } from './styled';

export interface TooltipProps {
	title?: string | JSX.Element;
	arrow?: boolean;
	placement?: string | Placement;
	children: ReactNode;
	width?: number;
	minWidth?: number;
	maxWidth?: number;
	onShow?: () => void;
}

interface ChildrenProps {
	onTouchStart: (a: TouchEvent) => void;
	onMouseEnter: (a: MouseEvent) => void;
	onMouseLeave: (a: MouseEvent) => void;
}

export { Placement as TooltipPlacement };

const Tooltip = React.forwardRef<HTMLElement, TooltipProps>((props: TooltipProps, ref) => {
	const { children } = props as {
		children: React.ReactElement<
			React.PropsWithChildren<ChildrenProps & { ref?: React.MutableRefObject<HTMLElement | undefined> }>
		>;
	};
	const [open, setOpen] = useState(false);
	const [childNode, setChildNode] = useState<HTMLElement>();
	const ignoreNonTouchEvents = React.useRef(false);
	const childrenRef = React.useRef<HTMLElement>();
	const [arrowRef, setArrowRef] = React.useState<HTMLDivElement | null>(null);
	const placement = (props.placement || Placement.Bottom) as Placement;
	const detectTouchStart = (event: TouchEvent) => {
		ignoreNonTouchEvents.current = true;

		const childrenProps = children?.props;
		if (childrenProps.onTouchStart) {
			childrenProps.onTouchStart(event);
		}
	};
	const childrenProps = {
		...children?.props,
		onTouchStart: detectTouchStart,
		ref: (node: HTMLElement) => {
			childrenRef.current = node;
			if ((children as any).ref) {
				(children as any).ref.current = node;
			}
		},
	};
	useEffect(() => {
		setChildNode(childrenRef.current);
	}, []);
	const handleEnter = () => {
		setOpen(true);
	};
	const handleLeave = () => {
		setOpen(false);
	};
	childrenProps.onMouseEnter = handleEnter;
	childrenProps.onMouseLeave = handleLeave;
	const skidding = [Placement.Top, Placement.Bottom].includes(placement)
		? 0
		: [Placement.TopEnd, Placement.BottomEnd].includes(placement)
		? 16
		: -16;
	const maxWidth = useMemo(
		() => Math.max(props.minWidth || 0, props.maxWidth || props.width || 0),
		[props.minWidth, props.maxWidth, props.width],
	);

	useEffect(() => {
		if (open) {
			props.onShow?.();
		}
	}, [open, props.onShow, props]);

	return (
		<>
			{React.cloneElement(children, childrenProps as ChildrenProps)}
			{open && (
				<Popper
					open={open}
					targetRef={childNode}
					arrowRef={arrowRef}
					placement={props.placement as Placement}
					modifiers={[
						{
							name: 'arrow',
							options: {
								padding: 16,
							},
						},
						{
							name: 'offset',
							options: {
								offset: [skidding, 5],
							},
						},
					]}
				>
					<Wrap show={open}>
						<Block
							style={{
								minWidth: props.minWidth,
								...(maxWidth ? { maxWidth } : {}),
								width: props.width,
							}}
						>
							{props.title}
						</Block>
						{props.arrow && (
							<Corner ref={setArrowRef}>
								<Arrow />
								<ArrowVert />
							</Corner>
						)}
					</Wrap>
				</Popper>
			)}
		</>
	);
});

export default Tooltip;
